import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Brand } from "@/components/Brand";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Bell, LogOut, Menu, ChevronRight } from "lucide-react";

export function DashboardLayout({ children, nav, title }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);

  const loadNotifications = async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifications(data);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    loadNotifications();
    const t = setInterval(loadNotifications, 20000);
    return () => clearInterval(t);
  }, [location.pathname]);

  const unread = notifications.filter((n) => !n.read).length;

  const markAll = async () => {
    await api.post("/notifications/read-all");
    loadNotifications();
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="h-16 flex items-center px-5 border-b border-border">
        <Brand />
      </div>
      <nav className="flex-1 py-4 overflow-y-auto">
        {nav.map((item) => {
          const active = location.pathname === item.to;
          const Icon = item.icon;
          return (
            <Link
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.key}`}
              className={`flex items-center gap-3 px-5 py-2.5 text-sm font-medium transition-colors border-l-2 ${
                active
                  ? "border-primary bg-accent text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:bg-secondary"
              }`}
            >
              <Icon className="h-[18px] w-[18px]" strokeWidth={1.6} />
              {item.label}
              {item.badge ? (
                <span className="ml-auto text-[10px] font-mono bg-primary text-primary-foreground px-1.5 py-0.5">
                  {item.badge}
                </span>
              ) : null}
            </Link>
          );
        })}
      </nav>
      <div className="p-4 border-t border-border">
        <div className="text-xs text-muted-foreground font-mono truncate">{user?.email}</div>
        <div className="text-[11px] uppercase tracking-wider text-primary mt-0.5">{user?.role}</div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-secondary/40 flex">
      {/* desktop sidebar */}
      <aside className="hidden lg:flex w-64 bg-card border-r border-border flex-col fixed inset-y-0">
        <SidebarContent />
      </aside>

      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen">
        {/* top header */}
        <header className="h-16 bg-card border-b border-border sticky top-0 z-30 flex items-center px-4 md:px-8 gap-3">
          <Sheet>
            <SheetTrigger asChild>
              <button className="lg:hidden p-2 -ml-2" data-testid="mobile-menu-btn">
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-64">
              <SidebarContent />
            </SheetContent>
          </Sheet>

          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span className="hidden sm:inline">Dashboard</span>
            <ChevronRight className="h-3.5 w-3.5 hidden sm:inline" />
            <span className="font-medium text-foreground">{title}</span>
          </div>

          <div className="ml-auto flex items-center gap-1">
            <DropdownMenu onOpenChange={(o) => o && unread && markAll()}>
              <DropdownMenuTrigger asChild>
                <button className="relative p-2 hover:bg-secondary transition-colors" data-testid="notifications-btn">
                  <Bell className="h-[18px] w-[18px]" strokeWidth={1.6} />
                  {unread > 0 && (
                    <span className="absolute top-1 right-1 h-4 min-w-4 px-1 bg-destructive text-destructive-foreground text-[10px] font-mono flex items-center justify-center rounded-full">
                      {unread}
                    </span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <DropdownMenuLabel>Notifications</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {notifications.length === 0 && (
                  <div className="px-3 py-6 text-center text-sm text-muted-foreground">No notifications</div>
                )}
                {notifications.slice(0, 8).map((n) => (
                  <div key={n.id} className="px-3 py-2 border-b border-border last:border-0">
                    <div className="text-sm font-medium">{n.title}</div>
                    <div className="text-xs text-muted-foreground">{n.message}</div>
                  </div>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 px-2 py-1.5 hover:bg-secondary transition-colors" data-testid="user-menu-btn">
                  <div className="h-8 w-8 bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">
                    {user?.name?.[0]?.toUpperCase()}
                  </div>
                  <span className="hidden md:inline text-sm font-medium max-w-[120px] truncate">{user?.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel className="truncate">{user?.name}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                  <LogOut className="h-4 w-4 mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 p-4 md:p-8">{children}</main>
      </div>
    </div>
  );
}
